import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BrandLogo from './BrandLogo';
import { fetchPublicSiteSettings } from '../cms/cmsData';
import { useSiteContent } from '../context/ContentContext';

const Navigation = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isDesktopNavHidden, setIsDesktopNavHidden] = useState(false);
  const [cmsSettings, setCmsSettings] = useState(null);
  const { content } = useSiteContent();
  const siteContent = cmsSettings || content.siteContent || {};
  const navigation = siteContent.navigation || {};
  const siteInfo = siteContent.siteInfo || {};
  const configuredLinks = Array.isArray(navigation.links)
    ? navigation.links.map((link) => ({
        label: link.label || link.text || '',
        path: link.path || link.link || link.href || '/',
      }))
    : [];
  const navLinks = configuredLinks.some((link) => link.path === '/blog')
    ? configuredLinks
    : [...configuredLinks, { label: 'Blog', path: '/blog' }];
  const ctaButton = {
    text: navigation.ctaButton?.text || '',
    link: navigation.ctaButton?.link || navigation.ctaButton?.path || '/resources',
  };

  useEffect(() => {
    let active = true;
    (async () => {
      const settings = await fetchPublicSiteSettings();
      if (active && settings) setCmsSettings(settings);
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let previousScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY <= 40) {
        setIsDesktopNavHidden(false);
      } else if (currentScrollY > previousScrollY + 8) {
        setIsDesktopNavHidden(true);
      } else if (currentScrollY < previousScrollY - 8) {
        setIsDesktopNavHidden(false);
      }

      previousScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <nav
      className={`fixed top-6 left-1/2 z-50 w-full max-w-5xl -translate-x-1/2 px-4 transition-transform duration-300 ${isDesktopNavHidden ? 'md:-translate-y-24' : 'md:translate-y-0'}`}
    >
      <div className="glass rounded-3xl border border-secondary/20 px-4 py-3 shadow-lg md:rounded-full md:px-6">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="min-w-fit"
            onClick={() => setIsOpen(false)}
          >
            <BrandLogo
              logoText={siteInfo.logo}
              logoImage={siteInfo.logoImage}
              alt={siteInfo.name || 'Sybil Esi'}
              className="h-10 w-[120px] object-contain md:w-[156px]"
              textClassName="text-2xl font-bold tracking-tight text-tertiary font-heading"
            />
          </Link>

          <div className="hidden items-center space-x-1 md:flex">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors font-body ${
                  location.pathname === link.path
                    ? 'bg-secondary text-white'
                    : 'text-sybil-textMuted hover:bg-white hover:text-tertiary'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:block">
            <Link
              to={ctaButton.link || '/contact'}
              className="flex items-center gap-2 rounded-full bg-secondary px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-secondary/20 transition-colors hover:bg-forestLight font-heading"
            >
              {ctaButton.text || 'Contact'}
            </Link>
          </div>

          <button
            type="button"
            className="rounded-full bg-white px-4 py-2 font-semibold text-tertiary md:hidden"
            onClick={() => setIsOpen((value) => !value)}
            aria-expanded={isOpen}
            aria-controls="mobile-nav-menu"
            aria-label="Toggle navigation menu"
          >
            Menu
          </button>
        </div>

        {isOpen ? (
          <div id="mobile-nav-menu" className="flex flex-col gap-2 pt-4 md:hidden">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`rounded-xl px-4 py-3 text-sm font-medium font-body ${
                  location.pathname === link.path ? 'bg-secondary text-white' : 'bg-white text-tertiary'
                }`}
              >
                {link.label}
              </Link>
            ))}

            <Link
              to={ctaButton.link || '/contact'}
              onClick={() => setIsOpen(false)}
              className="rounded-xl bg-secondary px-4 py-3 text-sm font-semibold text-white font-heading"
            >
              {ctaButton.text || 'Contact'}
            </Link>
          </div>
        ) : null}
      </div>
    </nav>
  );
};

export default Navigation;
